import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, concatAll, concatMap, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { CUSTOM_PRICING_MAP, FREE_PRICING_MAP } from 'app/constants/pricing.data';
import { extractDocVersion } from 'app/utils/document-version';
import { htmlEscapeDecode } from 'app/utils/escape-html';
import { isBomSubLine } from 'app/utils/is-bom-sub-line';
import {
  AppCustomPropertyInputArbonia as CustomPropertyInputArbonia,
  AppCustomLine as CustomLine,
  AppQuote as Quote,
  AppProductLine as ProductLine,
  Offer,
  OfferProductPosition,
  QuoteCustomPropertyKeyType,
  RevisionsStatus,
  RevisionsGroup,
  BranchOfferSettingsUpdateInput,
  PositionPricingUpdate,
  AppCustomLine
} from 'graphql/models/extended';
import {
  DocumentVersionInput,
  ImportLinesInput,
  LineInputType,
  OfferPreview,
  Customer,
  Object as Objekt,
  Account,
  Branch,
  ContactPerson,
  ConditionType,
  OfferSettingsUpdateInput,
  FileExportResult
} from 'graphql/models/generated';
import { CopyOfferMutation } from 'graphql/mutations/copy-offer';
import { CreateOfferMutation } from 'graphql/mutations/create-offer';
import { CreateOfferPreviewMutation } from 'graphql/mutations/create-offer-preview';
import { CreateOfferRevisionMutation } from 'graphql/mutations/create-offer-revision';
import { ImportLinesMutation } from 'graphql/mutations/import-lines';
import { LockOfferMutation } from 'graphql/mutations/lock-offer';
import { NormalizeLinesMutation } from 'graphql/mutations/normalize-lines';
import { SendOfferEmailMutation } from 'graphql/mutations/send-offer-email';
import { SetOfferStatusMutation } from 'graphql/mutations/set-offer-status';
import { SetOfferPartnerMutation } from 'graphql/mutations/set-offer-partner';
import { UpdateOfferExpirationMutation } from 'graphql/mutations/update-expiration-date';
import { UpdateOfferMutation } from 'graphql/mutations/update-offer';
import { UpdateOfferCommissionMutation } from 'graphql/mutations/update-offer-commission';
import { OfferQuery } from 'graphql/queries/offer';
import { OfferPricingStateQuery } from 'graphql/queries/offer-pricing-state';
import { OfferShortQuery } from 'graphql/queries/offer-short';
import { OfferVersionsQuery } from 'graphql/queries/offer-versions';
import { XmlExportQuery } from 'graphql/queries/xml-export';
import { AccountTypeENUM } from 'models/account.model';
import { ConditionTypeENUM } from 'models/condition.model';
import { ConfirmModalSettings } from 'models/modal.model';
import { OfferPreviewSettings } from 'models/offer-preview-settings';
import { SendOfferEmailResult, SendOfferEmailSettings } from 'models/offer-send-mail.model';
import { AnsweredRFPs, OfferBase } from 'models/offer.model';
import { LineSubtypePropVal } from 'models/position.model';
import { FileExportType } from 'models/snk-xml-export.model';
import { ConfirmModalService } from 'shared/services/confirm-modal.service';
import { CoreDataService } from 'shared/services/core-data.service';
import { OfferMapperService } from 'shared/services/offer-mapper.service';
import { OfferPositionProductService } from 'shared/services/offer-position-product.service';
import { OfferTitleService } from 'shared/services/offer-title.service';
import { PositionCustomPropertiesService } from 'shared/services/position-custom-properties.service';
import { PricingService } from 'shared/services/pricing.service';
import { StoreService } from 'shared/services/store.service';
import { CoreDataBuilderService } from './core-data-builder.service';
import { UpdateOfferTitleForUserMutation } from 'graphql/mutations/update-offer-title-for-user';
import { UpdateOfferTitleMutation } from 'graphql/mutations/update-offer-title';
import { JsonExportQuery } from 'graphql/queries/json-export';
import { OfferVersionsMinimalQuery } from 'graphql/queries/offer-versions-minimal';

@Injectable({
  providedIn: 'root'
})
export class OfferService {
  isDealer!: boolean;
  hasInitialCustomer: boolean = false;

  confirmModalSettings: ConfirmModalSettings = {
    cancel: 'MODALS.CALCULATIONS_CONDITION.CANCEL',
    confirm: 'MODALS.CALCULATIONS_CONDITION.CONFIRM',
    hideCloseIcon: true
  };

  constructor(
    private posCustomPropsService: PositionCustomPropertiesService,
    private offerQuery: OfferQuery,
    private offerPricingStateQuery: OfferPricingStateQuery,
    private offerShortQuery: OfferShortQuery,
    private createOfferMutation: CreateOfferMutation,
    private updateOfferMutation: UpdateOfferMutation,
    private updateOfferTitleMutation: UpdateOfferTitleMutation,
    private updateOfferTitleForUserMutation: UpdateOfferTitleForUserMutation,
    private copyOfferMutation: CopyOfferMutation,
    private updateOfferExpirationMutation: UpdateOfferExpirationMutation,
    private coreDataService: CoreDataService,
    private storeService: StoreService,
    private offerTitleService: OfferTitleService,
    private createOfferPreviewMutation: CreateOfferPreviewMutation,
    private offerVersionsQuery: OfferVersionsQuery,
    private offerVersionsMinimalQuery: OfferVersionsMinimalQuery,
    private createOfferRevisionMutation: CreateOfferRevisionMutation,
    private setOfferStatusMutation: SetOfferStatusMutation,
    private lockOfferMutation: LockOfferMutation,
    private sendOfferEmailMutation: SendOfferEmailMutation,
    private pricingService: PricingService,
    private offerMapperService: OfferMapperService,
    private offerPositionProductService: OfferPositionProductService,
    private xmlExportQuery: XmlExportQuery,
    private jsonExportQuery: JsonExportQuery,
    //private setPartnersMutation: SetPartnersMutation,
    private setOfferPartnerMutation: SetOfferPartnerMutation,
    private builderService: CoreDataBuilderService,
    private importLinesMutation: ImportLinesMutation,
    private normalizeLinesMutation: NormalizeLinesMutation,
    private confirmModalService: ConfirmModalService,
    private updateOfferCommissionMutation: UpdateOfferCommissionMutation,
    private translateService: TranslateService,
    private positionCustomPropertyService: PositionCustomPropertiesService
  ) {
    this.init();
  }

  init(): void {
    this.storeService.isCurrentAccountDealer().subscribe((isDealer) => {
      this.isDealer = isDealer;
    });
  }

  getOffer(documentVersion: DocumentVersionInput): Observable<Offer> {
    const variables = {
      documentVersion,
      filter: {}
    };

    return this.offerQuery.fetch(variables, { fetchPolicy: 'no-cache' }).pipe(
      map((r) => r.data?.quotes?.quote as Quote),
      map((quote: Quote) => ({
        ...quote,
        lines: (quote.lines || []).filter((p) => !this.posCustomPropsService.isOptionalLine(p))
      })),
      map((quote: Quote) => this.mapQuoteToOffer(quote))
    );
  }

  getOfferPricingState(documentVersion: DocumentVersionInput): Observable<OfferBase> {
    const variables = { documentVersion, filter: {} };

    return this.offerPricingStateQuery.fetch(variables, { fetchPolicy: 'no-cache' }).pipe<Quote, OfferBase>(
      map((r) => r.data?.quotes?.quote as Quote),
      map((quote: Quote) => quote as OfferBase)
    );
  }

  getOfferHeaderInfo(documentVersion: DocumentVersionInput): Observable<Offer> {
    const variables = { documentVersion, filter: {} };

    return this.offerShortQuery.fetch(variables, { fetchPolicy: 'no-cache' }).pipe<Quote, Offer>(
      map((r) => r.data?.quotes?.quote as Quote),
      map((quote: Quote) => this.mapQuoteToOffer(quote))
    );
  }

  getOfferVersions(documentId: string): Observable<Offer[]> {
    const variables = {
      expressions: {
        term: {
          field: 'DocumentId',
          value: documentId,
          comparisonType: 'Equal'
        }
      }
    };

    return this.offerVersionsQuery
      .fetch(variables, { fetchPolicy: 'no-cache' })
      .pipe<RevisionsGroup[]>(map((r) => r.data?.quotes?.groupedRevisions?.items as RevisionsGroup[]))
      .pipe<Quote[][], Quote[], Quote[], Offer[], Offer[]>(
        map((items: RevisionsGroup[]) => items.map((item: RevisionsGroup) => (item.revisions as Quote[]) || [])),
        concatAll(),
        map((quotes: Quote[]): Quote[] => quotes.map((quote: Quote): Quote => ({ ...quote, documentId: documentId }))),
        map((quotes: Quote[]): Offer[] => quotes.map((quote: Quote): Offer => this.mapQuoteToOffer(quote))),
        tap((offers: Offer[]) => this.storeService.setOfferVersions(offers))
      );
  }

  getMinimalOfferVersions(documentId: string): Observable<Offer[]> {
    const variables = {
      expressions: {
        term: {
          field: 'DocumentId',
          value: documentId,
          comparisonType: 'Equal'
        }
      }
    };

    return this.offerVersionsMinimalQuery
      .fetch(variables, { fetchPolicy: 'no-cache' })
      .pipe<RevisionsGroup[]>(map((r) => r.data?.quotes?.groupedRevisions?.items as RevisionsGroup[]))
      .pipe<Quote[][], Quote[], Quote[], Offer[]>(
        map((items: RevisionsGroup[]) => items.map((item: RevisionsGroup) => (item.revisions as Quote[]) || [])),
        concatAll(),
        map((quotes: Quote[]): Quote[] => quotes.map((quote: Quote): Quote => ({ ...quote, documentId: documentId }))),
        map((quotes: Quote[]): Offer[] => quotes.map((quote: Quote): Offer => this.mapQuoteToOffer(quote)))
      );
  }

  isOfferStatusClosed(): Observable<boolean> {
    return this.storeService.getOfferVersions().pipe(
      filter((revisions: Offer[]) => revisions && revisions.length > 0),
      map((revs: Offer[]) => revs[0].status === RevisionsStatus.CLOSED)
    );
  }

  closeCurrentOffer(offer: Offer): Observable<Offer> {
    return this.getMinimalOfferVersions(offer.documentId).pipe(
      switchMap((revs: Offer[]) =>
        forkJoin(
          revs
          .reverse()
          .map((rev) => this.pricingService.setOfferAutoUpdatePrices(offer as Offer, true).pipe(
              concatMap((o) => this.setOfferStatusAsString(
                { documentId: o.documentId, revision: rev.revision },
                RevisionsStatus.CLOSED
              ))
            )
          )
        )
      ),
      concatMap((): Observable<Offer> => this.lockOffer(offer))
    );
  }

  getCorrespondingRfpVersion(answeredRfp: Offer): Observable<Offer | undefined> {
    return this.storeService
      .getOfferVersions()
      .pipe(
        map((revisions: Offer[]) =>
          revisions.find(
            (revision: Offer) =>
              revision.locked &&
              revision.createdByType === AccountTypeENUM.DEALER &&
              revision.revision < answeredRfp.createdFromRevision
          )
        )
      );
  }

  getRfpVersion(): Observable<Offer | undefined> {
    return this.storeService
      .getOfferVersions()
      .pipe(map((revs: Offer[]) => (revs ? revs.find((offer: Offer) => this.isRfpSent(offer)) : undefined)));
  }

  isDealerOffer(offer: Pick<Offer | OfferBase, 'createdByType' | 'locked'>): boolean {
    const createdByType = offer.createdByType;
    const locked = offer.locked as boolean;

    return createdByType === AccountTypeENUM.DEALER && !locked;
  }

  isManufacturerOffer(offer: Pick<Offer | OfferBase, 'createdByType' | 'locked'>): boolean {
    const createdByType = offer.createdByType;
    const locked = offer.locked as boolean;

    return createdByType === AccountTypeENUM.MANUFACTURER && !locked;
  }

  isOfferStartedByDealer(offer: Pick<Offer, 'createdByType' | 'locked' | 'revision'>): boolean {
    return this.isDealerOffer(offer) && offer.revision === 0;
  }

  isOfferStartedByManufacturer(offer: Pick<Offer, 'createdByType' | 'locked' | 'revision'>): boolean {
    return this.isManufacturerOffer(offer) && offer.revision === 0;
  }

  canPreviewOffer(
    offer:
      | Pick<Offer, 'createdByType' | 'locked' | 'customer'>
      | Pick<OfferBase, 'createdByType' | 'locked' | 'customerId'>
  ): boolean {
    const isDealerOffer = this.isDealerOffer(offer);
    const isManufacturerOffer = this.isManufacturerOffer(offer);
    const hasCustomer = Boolean(('customerId' in offer && offer.customerId) || ('customer' in offer && offer.customer));

    return (this.isDealer && isDealerOffer && hasCustomer) || (!this.isDealer && isManufacturerOffer && hasCustomer);
  }

  isDealerSent(offer: Pick<Offer, 'status' | 'locked'>): boolean {
    const status = offer.status;
    const locked = offer.locked as boolean;

    return status === RevisionsStatus.SENT && locked;
  }

  isRfpSent(offer: Pick<Offer, 'createdByType' | 'locked'>): boolean {
    const createdByType = offer.createdByType;
    const locked = offer.locked as boolean;

    return createdByType === AccountTypeENUM.DEALER && locked;
  }

  isRfpAnswer(offer: Pick<Offer, 'createdByType' | 'locked' | 'revision'>): boolean {
    return this.isManufacturerOffer(offer) && offer.revision >= 0;
  }

  isRfpAnswered(offer: Pick<Offer, 'createdByType' | 'locked' | 'revision'>): boolean {
    const createdByType = offer.createdByType;
    const locked = offer.locked as boolean;
    const revision = offer.revision;

    return createdByType === AccountTypeENUM.MANUFACTURER && locked && revision >= 0;
  }

  mapQuoteToOffer(quote: Quote): Offer {
    const offer: Offer = {
      ...quote,
      productPositions: [],
      textTemplateId: (quote.textTemplateId && quote.textTemplateId.stringValue) || '',
      textTemplatePreText: (quote.textTemplatePreText && quote.textTemplatePreText.stringValue) || '',
      textTemplatePostText: (quote.textTemplatePostText && quote.textTemplatePostText.stringValue) || '',
      dealerOfferTitle: (quote.dealerOfferTitle && quote.dealerOfferTitle.stringValue) || '',
      manufacturerOfferTitle: (quote.manufacturerOfferTitle && quote.manufacturerOfferTitle.stringValue) || '',
      branchId: (quote.branchId && quote.branchId.stringValue) || '',
      snkVersion: `${quote.revision + 1}.0`,
      sendMessage: (quote.sendMessage && quote.sendMessage.stringValue) || '',
      calculationType: ((quote.calculationType && quote.calculationType.stringValue) ||
        ConditionTypeENUM.SURCHARGE) as ConditionType,
      isTempOffer: (quote.isTempOffer && quote.isTempOffer.booleanValue) || undefined,
      snkModifier: this.offerMapperService.mapSnkModifier(quote),
      createdFromRevision: (quote.createdFromRevision && quote.createdFromRevision.integerValue) || 0,
      offerPricingUpdate: quote.pricing,
      commission: {
        stringValue: quote.commission?.stringValue ?? ''
      },
      autoUpdatePrices: quote?.autoUpdatePrices,
      priceIsOutdated: quote?.priceIsOutdated
    };

    if (!offer.lines) {
      return offer;
    }
    // Apollo freezes cache-read results (dev-mode freeze, active in this prod build because
    // __DEV__/NODE_ENV is never set to production). The offer view-model mutates lines + their
    // customProperties in place across OfferPositionService (renumber, reorder, delete). Clone
    // those two layers up front so the model is writable and mutations don't throw
    // "Cannot assign to read only property" on frozen objects.
    // INTERIM safeguard — see follow-up ticket for the proper immutable refactor.
    const lines = offer.lines.map((line) => ({
      ...line,
      customProperties: line.customProperties?.map((prop) => ({ ...prop }))
    }));
    offer.lines = lines;

    // parent lines are all lines with LineID property set to 'SnkMain' - configurable product lines,
    // as well as "detached" lines (empty LineID) like non-configurable product lines (loose articles)
    // and custom lines (articles)
    const parentLines: Array<CustomLine | ProductLine> = lines.filter(
      (pos) =>
        (this.posCustomPropsService.isParentLine(pos) || this.posCustomPropsService.isDetachedLine(pos)) &&
        !isBomSubLine(pos)
    );
    offer.productPositions = parentLines.reduce((arr: OfferProductPosition[], parentLine: CustomLine | ProductLine) => {
      const lineSubtype = this.posCustomPropsService.getLineSubtype(parentLine);
      // loose article
      if (
        'product' in parentLine &&
        parentLine.product &&
        !parentLine.product.isConfigurable &&
        !isBomSubLine(parentLine)
      ) {
        const positionNumber = this.posCustomPropsService.getPositionNumber(parentLine);
        const descriptiveText = this.posCustomPropsService.getDescriptiveText(parentLine);
        const optionalOrAlternative = this.posCustomPropsService.isOptionalOrAlternativeLine(parentLine);

        return [
          ...arr,
          {
            product: this.offerPositionProductService.getProductBaseFromLine(parentLine),
            positions: [
              {
                ...parentLine,
                positionNumber,
                descriptiveText,
                optionalOrAlternative,
                lineSubtype
              }
            ]
          }
        ];
      }
      // free article
      if (lineSubtype === LineSubtypePropVal.FREE_ARTICLE && !isBomSubLine(parentLine)) {
        const positionNumber = this.posCustomPropsService.getPositionNumber(parentLine);
        const descriptiveText = this.posCustomPropsService.getDescriptiveText(parentLine);
        const optionalOrAlternative = this.posCustomPropsService.isOptionalOrAlternativeLine(parentLine);

        return [
          ...arr,
          {
            product: this.offerPositionProductService.getProductBaseFromLine(parentLine),
            positions: [
              {
                ...parentLine,
                positionNumber,
                descriptiveText,
                optionalOrAlternative,
                lineSubtype
              }
            ]
          }
        ];
      }
      // external article
      if (lineSubtype === LineSubtypePropVal.MY_ARTICLE && !isBomSubLine(parentLine)) {
        if (this.isDealer) {
          const positionNumber = this.posCustomPropsService.getPositionNumber(parentLine);
          const descriptiveText = this.posCustomPropsService.getDescriptiveText(parentLine);
          const optionalOrAlternative = this.posCustomPropsService.isOptionalOrAlternativeLine(parentLine);

          return [
            ...arr,
            {
              product: this.offerPositionProductService.getProductBaseFromLine(parentLine),
              positions: [
                {
                  ...parentLine,
                  positionNumber,
                  descriptiveText,
                  optionalOrAlternative,
                  lineSubtype
                }
              ]
            }
          ];
        }

        return arr;
      }
      if (!('product' in parentLine && parentLine.product && parentLine.product.isConfigurable)) {
        return arr;
      }
      // child lines are configurable product lines that belong to a parent line
      const childPositions = lines.filter(
        (p) => this.posCustomPropsService.getLineId(p) === parentLine.id && !isBomSubLine(p)
      ) as ProductLine[];

      let positions = [parentLine, ...childPositions];

      // configurable product
      positions.forEach((pos: ProductLine) => {
        const position = pos;

        const subPositions = lines.filter((p) => isBomSubLine(p) && p.parentLine?.id === position.id) as ProductLine[];

        if (subPositions) {
          position.subLines = subPositions;
        }

        const optionalOrAlternative = this.posCustomPropsService.isOptionalOrAlternativeLine(position);
        if (optionalOrAlternative) {
          position.optionalOrAlternative = optionalOrAlternative;
        }

        const positionNumber = this.posCustomPropsService.getPositionNumber(position);
        if (positionNumber) {
          position.positionNumber = positionNumber;
        }

        const descriptiveText = this.posCustomPropsService.getDescriptiveText(position);
        if (descriptiveText) {
          position.descriptiveText = descriptiveText;
        }

        if (position.configuration && position.configuration.sections) {
          position.product = this.offerPositionProductService.mapFilteredVariables(position, parentLine);
        }

        const lineSubType = this.posCustomPropsService.getLineSubtype(position);
        if (lineSubType) {
          position.lineSubtype = lineSubType;
        }

        return position;
      });

      positions = this.sortPositions(positions);

      return [
        ...arr,
        {
          product: this.offerPositionProductService.getProductBaseFromLine(positions[0]),
          positions
        }
      ];
    }, []);

    return offer;
  }

  sortPositions(positions: ProductLine[]): ProductLine[] {
    return positions.slice().sort((a, b) => {
      if (Number(a.positionNumber) > Number(b.positionNumber)) {
        return 1;
      }
      if (Number(a.positionNumber) < Number(b.positionNumber)) {
        return -1;
      }

      return 0;
    });
  }

  createOfferForObjekt(objekt: Objekt): Observable<string> {
    return this.createOffer(undefined, undefined, objekt);
  }

  createOfferForCustomer(customer: Customer): Observable<string> {
    const customerId = (customer && customer.id) || '';

    return this.coreDataService.getContactPersons(customerId, 1).pipe(
      mergeMap((contactPersons: ContactPerson[]) => {
        let contactPersonId;
        if (contactPersons && contactPersons.length) {
          contactPersonId = contactPersons[0].id;
        }

        return this.createOffer(customer, contactPersonId);
      })
    );
  }

  createOfferForContactPerson(customer: Customer, contactPersonId: string): Observable<string> {
    return this.createOffer(customer, contactPersonId);
  }

  createTempOffer(): Observable<Offer> {
    const properties: CustomPropertyInputArbonia[] = [
      {
        key: QuoteCustomPropertyKeyType.IsTempOffer,
        value: 'true',
        propertyType: 'Boolean'
      }
    ];

    return this.createOfferMutation.mutate({ properties }, { fetchPolicy: 'no-cache' }).pipe<Quote, Offer, Offer>(
      map((r) => r.data?.createQuote as Quote),
      map((quote: Quote) => this.mapQuoteToOffer(quote)),
      take(1)
    );
  }

  createOffer(customer?: Customer, contactPersonId?: string, object?: Objekt): Observable<string> {
    return combineLatest([
      this.storeService.getAccount().pipe(take(1)),
      this.storeService.getCurrentAccountBranch().pipe(take(1))
    ]).pipe(
      concatMap(([account, branch]: [Account, Branch]) => {
        const offerSettings = branch?.offerSettings;
        if (!offerSettings) {
          return '';
        }

        const { prefix1, prefix2, suffix } = offerSettings;

        if (!prefix1 || !prefix2 || !suffix) {
          return '';
        }

        const title = this.offerTitleService.generateOfferTitle(
          {
            id: '',
            customer,
            object,
            createdDate: '',
            revision: 0,
            snkCreator: account
          },
          branch.offerSettings
        );

        const variables = {
          title,
          customerId: (customer && customer.customerNumber) || '',
          contactPersonId,
          objektId: (object && object.id) || ''
        };

        return this.createOfferMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe<Quote, string>(
          map((r) => r.data?.createQuote as Quote),
          map((quote: Quote) => quote.documentId)
        );
      })
    );
  }

  updateBranchOfferSettings(branchId: string, data: OfferSettingsUpdateInput) {
    return this.builderService
      .update<
        Branch,
        BranchOfferSettingsUpdateInput
      >('Branch', branchId, { offerSettings: { update: data } }, 'BranchOfferSettingsFragment')
      .pipe(
        tap((branch: Branch) => {
          setTimeout(() => {
            this.storeService.setCurrentAccountBranch(branch);
          }, 1000);
        })
      );
  }

  createOfferRevision(offerBase: Pick<Offer, 'documentId' | 'revision'>): Observable<Offer> {
    const documentVersion = extractDocVersion(offerBase);
    const variables = { documentVersion };

    return this.createOfferRevisionMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe<RevisionsGroup, Offer, Offer>(
        map((r) => r.data?.quotes?.createRevision as RevisionsGroup),
        concatMap((res: any) => this.getOffer({ documentId: res.documentId, revision: res.latestRevision })),
        take(1)
      );
  }

  updateOfferWithCustomer(offerBase: Pick<Offer, 'documentId' | 'revision'>, item: Customer): Observable<Offer> {
    const props: CustomPropertyInputArbonia[] = [
      {
        key: QuoteCustomPropertyKeyType.CustomerID,
        value: item.customerNumber as string,
        propertyType: 'String'
      },
      {
        key: QuoteCustomPropertyKeyType.ContactPersonID,
        value: '',
        propertyType: 'String'
      }
    ];

    this.hasInitialCustomer = !!(offerBase as any).customerId;

    const documentVersion = extractDocVersion(offerBase);
    const customer = item;

    // update pricing on all lines, then update the offer
    return this.updateOffer(offerBase, props).pipe(
      concatMap(() => this.updatePricingBasedOnConditions(documentVersion, customer))
    );
  }

  updateManufacturerOfferWithCustomer(
    offerBase: Pick<Offer, 'documentId' | 'revision'>,
    item: Customer
  ): Observable<Offer> {
    const itemId = item.id as string;
    const props: CustomPropertyInputArbonia[] = [
      {
        key: QuoteCustomPropertyKeyType.BranchID,
        value: itemId,
        propertyType: 'String'
      },
      {
        key: QuoteCustomPropertyKeyType.ContactPersonID,
        value: '',
        propertyType: 'String'
      }
    ];

    this.hasInitialCustomer = !!(offerBase as any).customerId;

    if (!this.hasInitialCustomer) {
      // Skip the dialog and directly use clean pricing
      return this.setManufacturerOfferCustomerWithCleanPricing(offerBase, props, item, itemId);
    }

    return this.confirmModalService
      .show('CALCULATIONS_CONDITION', this.confirmModalSettings)
      .onAction()
      .pipe(
        concatMap((action: boolean) => {
          if (action) {
            return this.setManufacturerOfferCustomerWithOldPricing(offerBase, props, item, itemId);
          }

          return this.setManufacturerOfferCustomerWithCleanPricing(offerBase, props, item, itemId);
        })
      );
  }

  setManufacturerOfferCustomerWithOldPricing(
    offerBase: Pick<Offer, 'documentId' | 'revision'>,
    props: CustomPropertyInputArbonia[],
    item: Customer,
    itemId: string
  ): Observable<Offer> {
    return this.updateOffer(offerBase, props).pipe(
      concatMap((offer) =>
        this.setPartners(offerBase, itemId, itemId)
        .pipe(
          concatMap(() =>
            this.getOffer({documentId: offerBase.documentId, revision: offerBase.revision })
            .pipe(
              tap((q) => {
                try {
                  this.handleMeineArticles(q);
                } catch (e) {
                  console.error('handleMeineArticles threw', e);
                  throw e;
                }
              }),
              concatMap((quote) => this.setOfferPricingAndCustomer(offer, ({ ...quote }) as unknown as Quote, itemId, item)))
          )
        )
      ),
      map((offer: Offer) => this.setOfferBaseDiscountFromCustomer(offer)),
      tap((offer) => {
        this.storeService.setOfferUpdate(offer);
      })
    );
  }

  // TODO: need to research and optimize long call chains (see DIT-314)
  setManufacturerOfferCustomerWithCleanPricing(
    offerBase: Pick<Offer, 'documentId' | 'revision'>,
    props: CustomPropertyInputArbonia[],
    item: Customer,
    itemId: string
  ): Observable<Offer> {
    const excludeStepIds = [CUSTOM_PRICING_MAP.basePrice, CUSTOM_PRICING_MAP.listPrice, FREE_PRICING_MAP.listPrice];

    return this.updateOffer(offerBase, props).pipe(
      concatMap((offer: Offer) =>
        this.pricingService
        .removePricingCommands(offerBase, excludeStepIds).pipe(
          concatMap(() =>
            this.setPartners(offerBase, itemId, itemId)
              .pipe(
                concatMap(() =>
                  this.getOffer({documentId: offerBase.documentId, revision: offerBase.revision })
                    .pipe(
                      tap((q) => {
                        try {
                          console.log('before handleMeineArticles');
                          this.handleMeineArticles(q);
                          console.log('after handleMeineArticles');
                        } catch (e) {
                          console.error('handleMeineArticles threw', e);
                          throw e; // keep failing fast
                        }
                      }),
                      concatMap((newQuote) => this.setOfferPricingAndCustomer(offer, ({ ...newQuote }) as unknown as Quote, itemId, item)))
                )
              )
          )
        )
      ),
      map((offer: Offer) => this.setOfferBaseDiscountFromCustomer(offer)),
      tap((offer) => {
        this.storeService.setOfferUpdate(offer);
      })
    );
  }

  private handleMeineArticles(newQuote: Offer): void {
    if (!newQuote?.lines?.length) {
      this.storeService.setCurrentOfferMeineArticleIds({meineArticlePositionIds: []});
      return;
    }

    const meineArticlePositionIds = newQuote.lines
      .filter((e) =>
        this.positionCustomPropertyService.getLineSubtype(e as AppCustomLine) ===
        LineSubtypePropVal.MY_ARTICLE
      )
      .map((e) => e.id);

    this.storeService.setCurrentOfferMeineArticleIds({meineArticlePositionIds});
  }

  setOfferPricingAndCustomer(offer: Offer, quote: Quote, itemId: string, item: Customer): Observable<Offer> {
    return of({
      ...offer,
      positionPricingUpdate: this.pricingService.calculatePricingUpdate(quote),
      offerPricingUpdate: quote.pricing,
      partner: { externalId: itemId },
      customer: item,
      contactPerson: undefined
    } as Offer);
  }

  updateOfferTitle(targetUserId: string, docVersion: DocumentVersionInput, targetBranchId?: string): Observable<any> {
    let variables: any = { targetUserId: targetUserId, docVersion };
    if (targetBranchId) {
      variables = { ...variables, targetBranchId };
    }
    return this.updateOfferTitleForUserMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
  }

  updatePricingBasedOnConditions(docVersion: DocumentVersionInput, customer: Customer): Observable<Offer> {
    return this.getOffer(docVersion).pipe(
      concatMap((offer: Offer) =>
        this.coreDataService.getCustomerConditions(customer.id as string).pipe(
          concatMap((conditions) => {
            if (!conditions) {
              return of(offer);
            }

            if (!this.hasInitialCustomer) {
              // Skip modal if no initial customer
              return this.processOfferPricing(offer, conditions, customer);
            }

            // Show the modal if there is an initial customer
            return this.confirmModalService
              .show('CALCULATIONS_CONDITION', this.confirmModalSettings)
              .onAction()
              .pipe(
                concatMap((action: boolean) => {
                  if (action) {
                    return of(offer).pipe(
                      tap(() => {
                        this.storeService.setOfferUpdate({
                          customer: { ...customer },
                          contactPerson: undefined
                        });
                      })
                    );
                  }
                  return this.processOfferPricing(offer, conditions, customer);
                })
              );
          })
        )
      )
    );
  }

  private processOfferPricing(offer: Offer, conditions: any, customer: Customer): Observable<Offer> {
    const withPricing = offer.autoUpdatePrices;

    return this.pricingService.resetOfferCalculations(offer, offer.calculationType).pipe(
      concatMap(() =>
        this.pricingService.setOfferPricing(offer, conditions).pipe(
          concatMap((quote: Quote) =>
            this.pricingService
              .updateOfferPricing(offer, conditions.vat as number, quote.pricing?.sellVat?.stepId as string)
              .pipe(
                tap((quoteUpdate: Quote) => {
                  let offerPricingUpdate = quoteUpdate.pricing;
                  let positionPricingUpdate: PositionPricingUpdate | undefined;
                  if (!withPricing) {
                    const { discount, surcharge, type } = conditions;
                    const priceValue = (type === ConditionTypeENUM.SURCHARGE ? Number(surcharge) || 0 : Number(discount) || 0);
                    const priceKey = (type === ConditionTypeENUM.SURCHARGE) ? 'buyPriceSurcharge' : 'listPriceDiscount';

                    offerPricingUpdate = this.pricingService.setLocalOfferPricingByKey(offer, priceKey, priceValue);
                    positionPricingUpdate = this.pricingService.setLocalPositionsPricingByKey(offer, priceKey, priceValue, true);
                  } else {
                    positionPricingUpdate = this.pricingService.calculatePricingUpdate(quoteUpdate);
                  }

                  let updateOfferData: Partial<Offer> = {
                    priceIsOutdated: quoteUpdate.priceIsOutdated,
                    autoUpdatePrices: quoteUpdate.autoUpdatePrices
                  }

                  if (positionPricingUpdate) {
                    updateOfferData = {
                      ...updateOfferData,
                      positionPricingUpdate,
                      offerPricingUpdate,
                      calculationType: conditions.type,
                      customer: { ...customer, conditions },
                      contactPerson: undefined
                    }
                  }

                  this.storeService.setOfferUpdate(updateOfferData);
                })
              )
          )
        )
      ),
      map((quote: Quote) => ({ ...quote }) as unknown as Offer)
    );
  }

  updateOfferWithContactPerson(offer: Pick<Offer, 'documentId' | 'revision'>, item: ContactPerson): Observable<Offer> {
    const props: CustomPropertyInputArbonia[] = [
      {
        key: QuoteCustomPropertyKeyType.ContactPersonID,
        value: item.id as string,
        propertyType: 'String'
      }
    ];

    return this.updateOffer(offer, props).pipe(tap(() => this.storeService.setOfferUpdate({ contactPerson: item })));
  }

  updateOfferWithObjekt(
    offer: Pick<Offer, 'documentId' | 'revision' | 'oldObjectLogic'> & { title?: string },
    item: Objekt | null,
    isDealer: boolean
  ): Observable<Offer> {
    let props: CustomPropertyInputArbonia[] = [];
    if (offer.oldObjectLogic) {
      props.push({
        key: QuoteCustomPropertyKeyType.ObjectID,
        value: item ? item.id as string : "",
        propertyType: 'String'
      });
    } else {
      props.push({
        key: isDealer ? QuoteCustomPropertyKeyType.DealerObjectID : QuoteCustomPropertyKeyType.ManufacturerObjectID,
        value: item ? item.id as string : "",
        propertyType: 'String'
      });
    }

    const objectTitle = item?.name || '';
    let updatedOfferTitle = offer.title ?? '';

    return this.storeService.getCurrentAccountBranch().pipe(
      map((branch) => {
        if (branch?.offerSettings) {
          const prefixes = [branch.offerSettings.prefix1, branch.offerSettings.prefix2, branch.offerSettings.prefix3];

          const isObjectNumberAtPrefixPosition = prefixes.map((prefix) =>
            prefix?.type === 'OFFER_DATA' && prefix?.value === 'OBJECT_NR' ? 1 : 0
          );

          const prefixNoToReplace = isObjectNumberAtPrefixPosition.findIndex((flag) => flag === 1);

          let parts = updatedOfferTitle.split('-');

          // Check if that part is 'NV' and replace it with documentId
          if (parts[prefixNoToReplace] === 'NV') {
            parts[prefixNoToReplace] = objectTitle;
          }

          updatedOfferTitle = parts.join('-');
        }

        // Use the branch data to modify the title as needed
        updatedOfferTitle = offer?.title ? offer.title.replace('NV', objectTitle) : '';

        const variables = {
          documentVersion: {
            documentId: offer.documentId,
            revision: offer.revision
          },
          title: updatedOfferTitle
        };

        return { updatedOfferTitle, variables };
      }),
      switchMap(({ updatedOfferTitle, variables }) =>
        this.updateOffer(offer, props).pipe(
          tap(() => this.updateOfferTitleMutation.mutate(variables, { fetchPolicy: 'no-cache' }).subscribe()),
          tap(() => this.storeService.setOfferUpdate({ object: item ? item : undefined, title: updatedOfferTitle }))
        )
      )
    );
  }

  updateOfferTextTemplate(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    templateId: string,
    text: string,
    text2: string
  ): Observable<Offer> {
    const props: CustomPropertyInputArbonia[] = [
      {
        key: QuoteCustomPropertyKeyType.TextTemplateId,
        value: templateId,
        propertyType: 'String'
      },
      {
        key: QuoteCustomPropertyKeyType.TextTemplatePreText,
        value: htmlEscapeDecode(text),
        propertyType: 'String'
      },
      {
        key: QuoteCustomPropertyKeyType.TextTemplatePostText,
        value: htmlEscapeDecode(text2),
        propertyType: 'String'
      }
    ];

    return this.updateOffer(offer, props).pipe(
      tap(() =>
        this.storeService.setOfferUpdate({
          textTemplateId: templateId,
          textTemplatePreText: text,
          textTemplatePostText: text2
        })
      )
    );
  }

  updateOffer(
    { documentId, revision }: Pick<Offer, 'documentId' | 'revision'>,
    properties?: CustomPropertyInputArbonia[],
    lines?: LineInputType[]
  ): Observable<Offer> {
    const documentVersion: DocumentVersionInput = { documentId, revision };
    const variables = { documentVersion, properties, lines };

    return this.updateOfferMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe<Quote, Offer, Offer, Offer>(
      map((r) => r.data?.custom?.updateQuote as Quote),
      map((quote: Quote) => this.mapQuoteToOffer(quote)),
      tap((updatedOffer: Offer) =>
        this.storeService.setOfferUpdate({
          status: updatedOffer.status,
          snkModifier: updatedOffer.snkModifier,
          commission: updatedOffer.commission
        })
      ),
      take(1)
    );
  }

  setPartners(
    { documentId, revision }: Pick<Offer, 'documentId' | 'revision'>,
    partnerExternalId: string,
    soldToExternalId: string
  ): Observable<any> {
    const documentVersion: DocumentVersionInput = { documentId, revision };
    const variables = { documentVersion, partnerExternalId, soldToExternalId };

    return this.setOfferPartnerMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe(
      map((r) => {
        return r.data?.setOfferPartner;
      }),
      take(1)
    );
  }

  copyOffer({ documentId, revision }: Pick<Offer, 'documentId' | 'revision'>): Observable<RevisionsGroup> {
    const documentVersion: DocumentVersionInput = { documentId, revision };
    const variables = { documentVersion };

    return this.copyOfferMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe<RevisionsGroup, RevisionsGroup>(
      map((r) => r.data?.quotes?.copy as RevisionsGroup),
      take(1)
    );
  }

  updateOfferExpiration(offer: Pick<Offer, 'documentId' | 'revision'>, expirationDate: string): Observable<Offer> {
    const documentVersion = extractDocVersion(offer);
    // remove timezone from ISO string
    const expDate = expirationDate.substring(0, 10);
    const variables = { documentVersion, expirationDate: expDate };

    return this.updateOfferExpirationMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe<Quote, Offer, Offer, Offer>(
        map((r) => r.data?.custom?.editExpirationDate as Quote),
        map((quote: Quote) => this.mapQuoteToOffer(quote)),
        tap((updatedOffer: Offer) =>
          this.storeService.setOfferUpdate({
            validTo: expDate,
            status: updatedOffer.status,
            snkModifier: updatedOffer.snkModifier
          })
        ),
        take(1)
      );
  }

  createOfferPreview(offerDocumentId: DocumentVersionInput, settings: OfferPreviewSettings): Observable<any> {
    const language = this.translateService.currentLang || this.translateService.defaultLang;
    const variables = { offerDocumentId, settings, language };

    return this.createOfferPreviewMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe<OfferPreview, any>(
      // TODO MIG: types changed, check if correct
      map((r) => r.data?.createOfferPreview as OfferPreview),
      take(1)
    );
  }

  setOfferStatus(offer: Pick<Offer, 'documentId' | 'revision'>, status: string): Observable<Offer> {
    return this.setOfferStatusAsString(offer, status).pipe<Offer, Offer>(
      map((quote: Quote) => this.mapQuoteToOffer(quote)),
      tap((updatedOffer: Offer) => this.storeService.setOfferUpdate({
        status: updatedOffer.status
      }))
    );
  }

  private setOfferStatusAsString(offer: Pick<Offer, 'documentId' | 'revision'>, status: string): Observable<Quote> {
    const documentVersion = extractDocVersion(offer);
    const variables = { documentVersion, status };

    return this.setOfferStatusMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe<Quote, Quote>(
      map((r) => r.data?.quotes?.modify?.setStatusAsString as Quote),
      take(1)
    );
  }

  lockOffer(offer: OfferBase): Observable<Offer> {
    const documentVersion = extractDocVersion(offer);
    const variables = { documentVersion };

    return this.lockOfferMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe<RevisionsGroup, Quote, Offer, Offer, Offer>(
        map((r) => r.data?.quotes?.lock as RevisionsGroup),
        map(
          (res: RevisionsGroup): Quote =>
            (res.revisions as Quote[]).find((r: Quote) => r.revision === documentVersion.revision) as Quote
        ),
        map((quote: Quote) => this.mapQuoteToOffer(quote)),
        tap((updatedOffer: Offer) => this.storeService.setOfferUpdate(updatedOffer)),
        take(1)
      );
  }

  sendOfferEmail(
    offerDocumentId: DocumentVersionInput,
    settings: SendOfferEmailSettings
  ): Observable<SendOfferEmailResult> {
    const variables = { offerDocumentId, settings };

    return this.sendOfferEmailMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe<SendOfferEmailResult>(map((r) => r.data?.sendOfferEmail as SendOfferEmailResult));
  }

  getAnsweredRFPsForOffer(): Observable<AnsweredRFPs[]> {
    return this.storeService
      .getOfferVersions()
      .pipe(concatMap((offers: Offer[]) => of(this.prepareAnsweredRFPsList(offers))));
  }

  prepareAnsweredRFPsList(offers: Offer[]): AnsweredRFPs[] {
    return (offers || []).reduce((acc: AnsweredRFPs[], offer: Offer): AnsweredRFPs[] => {
      const isRfpAnswered = this.isRfpAnswered(offer);
      if (isRfpAnswered) {
        const obj = this.buildAnsweredRFPsListObject(offer);
        acc.push(obj);
      }

      return acc;
    }, []);
  }

  buildAnsweredRFPsListObject(offer: Offer): AnsweredRFPs {
    const totalSellPrice = offer.pricing && offer.pricing.totalSellPrice ? offer.pricing.totalSellPrice.value : 0;

    const obj: AnsweredRFPs = {
      title: offer.title,
      revision: offer.revision,
      modifier: offer.snkModifier.name,
      totalPrice: totalSellPrice,
      date: offer.modifiedDate,
      active: false
    };

    return obj;
  }

  updateOfferCommission(documentVersion: DocumentVersionInput, commission: string) {
    const variables = { documentVersion, commission };

    return this.updateOfferCommissionMutation.mutate(variables, { fetchPolicy: 'no-cache' });
  }

  xmlExport(documentVersion: DocumentVersionInput, type: FileExportType): Observable<FileExportResult> {
    const variables = { documentVersion, type, variation: '' };

    return this.xmlExportQuery.fetch(variables, { fetchPolicy: 'no-cache' }).pipe<FileExportResult, FileExportResult>(
      map((r) => r.data?.xmlExport),
      take(1)
    );
  }

  jsonExport(documentVersion: DocumentVersionInput, type: FileExportType): Observable<FileExportResult> {
    const variables = { documentVersion, type };

    return this.jsonExportQuery.fetch(variables, { fetchPolicy: 'no-cache' }).pipe<FileExportResult, FileExportResult>(
      map((r) => r.data?.jsonExport),
      take(1)
    );
  }

  importLines(
    offer: Pick<Offer, 'documentId' | 'revision'>,
    sources: ImportLinesInput[]
  ): Observable<Pick<ProductLine, 'id'>[]> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      sources
    };

    return this.importLinesMutation
      .mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe(map((r) => r.data?.quotes?.modify?.importLines?.newLines as Pick<ProductLine, 'id'>[]));
  }

  normalizeLines(offer: Pick<Offer, 'documentId' | 'revision'>, lineIds: string[]): Observable<Quote | null> {
    const variables = {
      documentVersion: extractDocVersion(offer),
      lineIds
    };

    return this.normalizeLinesMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe(
      map((r) => r.data?.normalizeLines as Quote),
      catchError((error) => {
        console.error('Error on normalizeLines:', error);

        return of(null);
      })
    );
  }

  setOfferBaseDiscountFromCustomer(offer: Offer): Offer {
    const offerData: Offer = { ...offer };
    if (
      offerData.offerPricingUpdate &&
      offerData.offerPricingUpdate.baseDiscount &&
      offerData.offerPricingUpdate.baseDiscount.value !== undefined &&
      offerData.customer &&
      offerData.customer.baseDiscount
    ) {
      offerData.offerPricingUpdate.baseDiscount.value = Number(offerData.customer.baseDiscount);
    }

    return offerData;
  }

  getCurrentOfferTitle(offer: Offer, isDealer: boolean): string {
    return (isDealer
      ? offer.dealerOfferTitle
      : offer.manufacturerOfferTitle) || offer.title;
  }
}
